import { useEffect } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogHeader, ImageCarousel, Typography } from 'ui';

import { AiFiltersAndBreakdownSidebar } from '../../../evidence/index';
import { AnnotatedMediaItem, useAnnotations } from '../../../quality-report/index';
import { type PreviewGalleryImage } from '../../types';

export const PreviewImageReview = ({
  open,
  onClose,
  selectedImage,
  onDeleteImage,
  showDeleteButton = true,
  isPortrait,
  showAerosolAnnotation = false,
  showAerosolBreakdown = false,
}: {
  open: boolean;
  onClose: () => void;
  selectedImage: PreviewGalleryImage;
  onDeleteImage: () => void;
  showDeleteButton?: boolean;
  isPortrait: boolean;
  showAerosolAnnotation?: boolean;
  showAerosolBreakdown?: boolean;
}) => {
  const shouldShowMessage = selectedImage.status !== 'success' && selectedImage.message;

  const {
    handleImageChange,
    selectedAnnotation,
    hiddenCategories,
    showLegend,
    handleHideCategory,
    handleToggleLegend,
    showSidebar,
  } = useAnnotations({ initialAnnotations: selectedImage.annotations });

  useEffect(() => {
    if (selectedImage.annotations) {
      handleImageChange(selectedImage.annotations);
    }
  }, [selectedImage.annotations, handleImageChange]);

  const showAIFiltersAndBreakdown = !!(
    showSidebar &&
    selectedImage.status === 'success' &&
    selectedAnnotation &&
    selectedImage.aiModel
  );

  return (
    <Dialog open={open} onClose={onClose} fullScreen={true}>
      <DialogHeader title="" onClose={onClose} />
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: isPortrait ? 'column' : 'row',
          gap: 2,
          padding: 0,
          paddingTop: 1,
        }}
      >
        <ImageCarousel
          photoEvidence={[selectedImage.src]}
          slotProps={{
            mediaItem: {
              alt: 'Evidence photo',
              omitPadding: true,
            },
            mediaItemSlot: (
              <Box display="flex" flexDirection="column" justifyContent="space-between">
                {selectedAnnotation && selectedImage.aiModel ? (
                  <AnnotatedMediaItem
                    image={selectedImage.src}
                    annotations={selectedAnnotation}
                    hiddenCategories={hiddenCategories}
                    showLegend={showLegend}
                    model={selectedImage.aiModel}
                  />
                ) : null}
                <Box paddingLeft={2}>
                  {shouldShowMessage ? <Typography>{selectedImage.message}</Typography> : null}
                  {selectedImage.feedbackMessages?.length ? (
                    <ul>
                      {selectedImage.feedbackMessages?.map((message) => (
                        <li key={message}>
                          <Typography>{message}</Typography>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </Box>
              </Box>
            ),
            sidebar:
              // even if I already check this in showAIFiltersAndBreakdown,
              // I need to check again here because typescript doesn't know that selectedImage.aiModel is defined
              showAIFiltersAndBreakdown && selectedImage.aiModel
                ? {
                    children: (
                      <AiFiltersAndBreakdownSidebar
                        selectedAnnotation={selectedAnnotation}
                        hiddenCategories={hiddenCategories}
                        handleHideCategory={handleHideCategory}
                        handleToggleLegend={handleToggleLegend}
                        showLegend={showLegend}
                        showSwitch={true}
                        model={selectedImage.aiModel}
                        showAerosolAnnotation={showAerosolAnnotation}
                        showAerosolBreakdown={showAerosolBreakdown}
                        attachmentDetails={selectedImage?.attachmentDetails ?? undefined}
                        targetWeightPercentage={selectedImage?.targetWeightPercentage ?? undefined}
                      />
                    ),
                    label: 'Adjust AI Filters',
                  }
                : undefined,
          }}
        />
      </DialogContent>
      {showDeleteButton ? (
        <DialogActions sx={{ justifyContent: 'space-between', padding: 2 }}>
          <Button onClick={onDeleteImage} disabled={selectedImage?.disableDelete} fullWidth>
            Delete & retake
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
};
